import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtMoney, STAGE_META, fmtDate, isAdmin } from '../api';
import { useAuth } from '../AuthContext';
import { Banknote, Briefcase, TrendingUp, AlertCircle, CalendarClock, ArrowRight } from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/dashboard').then((r) => setData(r.data)).catch((e) => setErr(e?.message || 'Failed to load'));
  }, []);

  if (err) return <div className="crm-card" style={{ color: '#9f1239' }}>{err}</div>;
  if (!data) return <div className="crm-card">Loading…</div>;

  const stages = Object.entries(data.by_stage || {});
  const admin = isAdmin(user);

  return (
    <div data-testid="crm-dashboard">
      <div className="crm-kpi-grid">
        <Kpi label="Open Deals" value={data.open_deals_count} sub={admin ? `${fmtMoney(data.open_deals_value)} pipeline` : 'In progress'} icon={Briefcase} cls="k-indigo" />
        {admin && <Kpi label="Revenue (signed)" value={fmtMoney(data.total_revenue)} sub={`Cost ${fmtMoney(data.total_cost)}`} icon={Banknote} cls="k-emerald" />}
        {admin && <Kpi label="Gross Margin" value={fmtMoney(data.total_margin)} sub={data.total_revenue ? `${Math.round((data.total_margin / data.total_revenue) * 100)}% margin` : '—'} icon={TrendingUp} cls="k-amber" />}
        {admin && <Kpi label="Payments — This Month" value={fmtMoney(data.payments_this_month)} sub={`All-time ${fmtMoney(data.payments_all_time)}`} icon={Banknote} cls="k-emerald" />}
        <Kpi label="Due Today" value={data.due_today} sub={`${data.overdue} overdue`} icon={CalendarClock} cls={data.overdue > 0 ? 'k-rose' : 'k-indigo'} />
        <Kpi label="Won Deals" value={data.won_count} sub={admin ? fmtMoney(data.won_value) : 'Closed'} icon={TrendingUp} cls="k-emerald" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginTop: 18 }} className="crm-grid-stack">
        <div className="crm-card">
          <h3>Pipeline by Stage</h3>
          {stages.length === 0
            ? <p style={{ color: 'var(--crm-muted)', fontSize: 13 }}>No deals yet. Add your first lead from the Pipeline page.</p>
            : (
              <div>
                {stages.map(([stage, info]) => {
                  const meta = STAGE_META[stage] || { label: stage, color: '#64748b' };
                  const pct = data.open_deals_count > 0 ? Math.round((info.count / Math.max(...stages.map(([, i]) => i.count))) * 100) : 0;
                  return (
                    <div key={stage} style={{ marginBottom: 10 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 4 }}>
                        <span style={{ color: 'var(--crm-ink)', fontWeight: 700 }}>{meta.label}</span>
                        <span style={{ color: 'var(--crm-muted)' }}>{info.count}{admin ? ` · ${fmtMoney(info.value)}` : ''}</span>
                      </div>
                      <div style={{ background: '#f1f5f9', borderRadius: 999, height: 8, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.max(pct, 4)}%`, height: '100%', background: meta.color, borderRadius: 999 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
        </div>

        <div className="crm-card">
          <h3>Recent Deals</h3>
          {(!data.recent_deals || data.recent_deals.length === 0)
            ? <p style={{ color: 'var(--crm-muted)', fontSize: 13 }}>No deals yet.</p>
            : data.recent_deals.map((d) => {
              const meta = STAGE_META[d.stage] || { label: d.stage, color: '#64748b', light: '#e2e8f0' };
              return (
                <Link key={d.id} to={`/crm/deals/${d.id}`} style={{ display: 'block', padding: '10px 0', borderBottom: '1px solid #f1f5f9', textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 13.5 }}>{d.company}</div>
                      <div style={{ color: 'var(--crm-muted)', fontSize: 12 }}>{d.project_name || '—'} · {fmtDate(d.created_at)}</div>
                    </div>
                    <span className="crm-pill" style={{ background: meta.light, color: meta.color }}>
                      <span className="dot" style={{ background: meta.color }} />{meta.label}
                    </span>
                  </div>
                </Link>
              );
            })}
          <Link to="/crm/pipeline" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 10, color: 'var(--crm-indigo)', fontWeight: 700, fontSize: 12.5 }}>
            Go to pipeline <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      <style>{`@media (max-width: 980px) { .crm-grid-stack { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, cls = '' }) {
  return (
    <div className={`crm-kpi ${cls}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span className="label">{label}</span>
        {Icon && <Icon size={16} color="#94a3b8" />}
      </div>
      <div className="value">{value ?? '—'}</div>
      <div className="delta">{sub}</div>
    </div>
  );
}
